import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Send, Loader2, Sparkles, X as XIcon, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * One-row Post-Meeting Email composer.
 * Opens a dialog where the user picks assets the biotech wants to start
 * with, optionally tweaks the meeting notes, previews the Claude-generated
 * email, and fires it off via Gmail.
 *
 * Bulk send: if `bulkMode` prop is true the button label changes to "Send
 * to all selected"; expects `bulkRows` array of met-in-person rows.
 */
export default function PostMeetingEmailButton({ row, onSent }) {
  const [open, setOpen] = useState(false);
  const [biotechAssets, setBiotechAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  // Eligible: biotech-linked + has email
  const eligible = row.match_status === 'linked' && row.email && row.biotech_company_id;

  useEffect(() => {
    if (!open || !row.biotech_company_id) return;
    axios.get(`${API}/assets?company_id=${row.biotech_company_id}`, { withCredentials: true })
      .then(r => setBiotechAssets(r.data?.items || r.data || []))
      .catch(() => setBiotechAssets([]));
    setSelectedAssets(row.assets_discussed || []);
    setNotes(row.notes || '');
    setPreview(null);
  }, [open, row.biotech_company_id]); // eslint-disable-line

  const persistAssets = async (ids) => {
    try {
      await axios.patch(`${API}/met/${row.id}/assets-discussed`, { asset_ids: ids }, { withCredentials: true });
    } catch (e) { /* non-blocking */ }
  };

  const toggleAsset = (id) => {
    const next = selectedAssets.includes(id)
      ? selectedAssets.filter(a => a !== id)
      : [...selectedAssets, id];
    setSelectedAssets(next);
    persistAssets(next);
  };

  const previewEmail = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${API}/met/post-meeting-email/preview`,
        { row_id: row.id, asset_ids: selectedAssets, custom_notes: notes },
        { withCredentials: true },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const sendEmail = async (testMode = false) => {
    const testEmail = testMode ? (window.prompt('Send TEST email to which address?', 'amit.dang@foresightrx.com') || '').trim() : '';
    if (testMode && !testEmail) return;
    if (!testMode && !window.confirm(`Send this email to ${row.email}?\n\nIt will be sent from amit.dang@foresightrx.com and an Outreach row will be created.`)) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/met/post-meeting-email/send`,
        {
          items: [{ row_id: row.id, asset_ids: selectedAssets, custom_notes: notes }],
          ...(testEmail ? { test_email_override: testEmail } : {}),
        },
        { withCredentials: true },
      );
      toast.success(testMode
        ? `Test queued — will arrive at ${testEmail} in a few seconds`
        : `Queued — task ${r.data.task_id} · the send fires in the background`);
      if (!testMode) { setOpen(false); onSent?.(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  if (!eligible) {
    return (
      <button
        disabled
        className="p-1 text-slate-300 cursor-not-allowed"
        title={!row.biotech_company_id ? 'Link this contact to a biotech first' : !row.email ? 'Add an email first' : 'Only biotech-linked contacts can send post-meeting emails'}
      >
        <Sparkles className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded"
        title="Send a post-meeting follow-up email (Claude-personalised)"
        data-testid={`met-post-meeting-email-${row.id}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-meeting-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Post-Meeting Email — {row.name || row.email}
            </DialogTitle>
            <DialogDescription>
              {row.company_name} · {row.email} · auto-personalised with Claude using your meeting notes + the selected assets
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Asset multi-select */}
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Assets they want to begin with</label>
              {biotechAssets.length === 0 ? (
                <div className="text-xs text-slate-400 italic mt-1">No assets in CRM yet — email will fall back to &quot;your asset&quot;</div>
              ) : (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {biotechAssets.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAsset(a.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        selectedAssets.includes(a.id)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                      }`}
                      data-testid={`asset-pick-${a.id}`}
                    >
                      {a.asset_name || a.name || 'Unnamed'}
                      {a.stage && <span className={`ml-1.5 text-[9px] ${selectedAssets.includes(a.id) ? 'opacity-80' : 'text-slate-400'}`}>{a.stage}</span>}
                    </button>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-1">{selectedAssets.length} selected. Saved as you click.</div>
            </div>

            {/* Meeting notes */}
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Meeting notes (sent verbatim in the email)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mt-1 text-sm"
                placeholder="e.g. Met at booth 1247. Looking for Korean/Japanese pharma BD intros for their Phase 2 dry-eye program. Open to non-exclusive, success-based engagement."
                data-testid="post-meeting-email-notes"
              />
            </div>

            {/* Preview */}
            <div className="flex items-center gap-2">
              <Button onClick={previewEmail} disabled={previewing} variant="outline" size="sm" data-testid="post-meeting-email-preview-btn">
                {previewing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…</> : <>Preview email</>}
              </Button>
              <Button onClick={() => sendEmail(true)} disabled={sending || !preview} variant="outline" size="sm" data-testid="post-meeting-email-test-btn" className="border-amber-300 text-amber-800 hover:bg-amber-50">
                <Mail className="w-3 h-3 mr-1" /> Send test to me
              </Button>
              <Button onClick={() => sendEmail(false)} disabled={sending || !preview} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="post-meeting-email-send-btn">
                {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</> : <><Send className="w-3 h-3 mr-1" /> Send via Gmail</>}
              </Button>
            </div>

            {preview && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b text-xs flex items-center justify-between">
                  <span><span className="text-slate-500">To:</span> <b className="text-slate-700">{preview.to}</b></span>
                  <Badge variant="outline" className="text-[10px]">amit.dang@foresightrx.com</Badge>
                </div>
                <div className="px-4 py-2 border-b text-sm text-slate-900 font-medium">{preview.subject}</div>
                <div className="px-4 py-3 text-sm text-slate-700 max-h-[400px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.body_html || '', { ADD_ATTR: ['target'] }) }} />
                {preview.ai_paragraph && (
                  <div className="px-4 py-2 bg-emerald-50/40 border-t text-[11px] text-emerald-800">
                    <Sparkles className="w-3 h-3 inline mr-1" /> Claude paragraph: <span className="italic">{preview.ai_paragraph}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
